import {useAuth} from "../../../../hooks/useAuth";
import {Link, useNavigate} from "react-router-dom";
import React, {useCallback, useEffect, useRef, useState} from "react";
import CustomDataTable from "../../../../shared/tables/CustomDataTable";
import {APIGetCustomSubcategoriesArchived} from "../api/customSubcategories";
import {APIRestoreCustomSubcategory} from "../api/customSubcategory";
import Box from "@mui/material/Box";
import moment from "moment/moment";
import GenericButton from "../../../../shared/ui/GenericButton";
import useDataTable from "../../../../hooks/useDataTable";
import {ISubcategory, SUBCATEGORIES_COLUMNS, SUBCATEGORIES_COLUMNS_PRINCIPAL} from "./SubcategoriesActive";

const SubcategoriesArchived = () => {
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const isTeacher = user.position === "teacher";

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const getSubcategories = (currentPage = pageRef.current) => {
        APIGetCustomSubcategoriesArchived({page: `?page=${currentPage + 1}`, sort: orderBy, order: order, search: submittedQuery})
            .then((response) => {
                if (response.data.success) {
                    const pageData = response.data.data;

                    if(pageData.data.length === 0 && currentPage > 0) {
                        const PreviousPage = currentPage - 1;

                        setPage(PreviousPage);
                        getSubcategories();
                        return;
                    }

                    setTableData(response.data.data.data.data.map((subcategory: any) => mapSubcategories(subcategory)));
                    setTotalRows(response.data.data.data.total);
                    setPerPage(response.data.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => {
                // console.log("getTeachers ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
    }

    const mapSubcategories = useCallback((subcategory: ISubcategory) => {

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        disabled={user.id !== subcategory.user_id}
                        type="edit-small"
                        onClick={() => editItemHandler(subcategory.id)}
                    />
                </Box>
                <Box>
                    <GenericButton
                        disabled={user.id !== subcategory.user_id}
                        type="archive"
                        onClick={() => confirmRestore(subcategory)}/>
                </Box>
            </Box>
        );

        return {
            id: subcategory.id,
            name: <Link to={`/subcategorii-proprii/${subcategory.id}`}>{subcategory.name}</Link>,
            subject: subcategory.subject,
            grade: subcategory.grade,
            created_at: moment(subcategory.created_at).locale("ro").format("DD.MM.YYYY"),
            ...(isTeacher && {actions})
        }
    }, [user.id]);

    useEffect(() => {
        setLoading(true);
        getSubcategories();
    }, [page, order, orderBy, submittedQuery]);

    useEffect(() => {
        const columns = isTeacher ? SUBCATEGORIES_COLUMNS : SUBCATEGORIES_COLUMNS_PRINCIPAL;
        setTableColumns(columns);
    }, [user.position]);

    const editItemHandler = (id: number) => {
        navigate(`/subcategorii-proprii/${id}/editare`);
    };

    const restoreCategory = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});
        APIRestoreCustomSubcategory({id})
            .then((response) => {
                if (response.data.success) {
                    // console.log("deleteTeacher OK", response);
                    getSubcategories(pageRef.current);
                } else {
                    update("error", {show: true, code: "A002"});
                }
                callback();
            })
            .catch((err) => {
                // console.log("deleteTeacher ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => update("loading", {show: false}));
    };

    const confirmRestore = (item: {
        id: number;
        name: string;
        [key: string]: any;
    }) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: "Ești sigur că vrei să dezarhivezi această subcategorie?",
            message: (
                <>
                    Subcategoria <b>{item.name}</b> va fi dezarhivată. Veți regăsi această subcategorie în
                    secțiunea <b>Subcategorii active.</b>
                </>
            ),
            returnFunction: (callback: Function) => {
                restoreCategory(item.id.toString(), callback);
            },
            buttonYes: "Dezarhivează",
            buttonNo: "Închide",
        });
    };

    return (
        <CustomDataTable
            loading={loading}
            columns={tableColumns}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onSearch={handleSearch}
            onChange={handleSearchChange}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    );
}

export default SubcategoriesArchived;